/**
 * Strategy Discovery Engine
 *
 * Systematically explores combinations of betting rules (side, streak length,
 * progression, stop-loss, take-profit) and ranks them by backtested performance.
 */

import type { Strategy, Rule, SimulationConfig, BacktestResults, BetSide, ProgressionMethod } from '../types'
import { runSimulation } from './simulator'

// ────────────────────────────────────────────────────────────
// Candidate space
// ────────────────────────────────────────────────────────────

const BET_SIDES:    BetSide[]           = ['Banker', 'Player']
const STREAK_LENS:  number[]            = [1, 2, 3, 4]
const PROGRESSIONS: ProgressionMethod[] = ['flat', 'martingale', 'fibonacci', 'dalembert', '1326']
const STOP_LOSSES:  number[]            = [200, 300, 500, 750]   // $ amounts
const TAKE_PROFITS: number[]            = [200, 400, 600, 1000]  // $ amounts

export interface DiscoveryCandidate {
  id: string
  label: string
  strategy: Strategy
  results?: BacktestResults
  score: number              // composite rank score
}

export interface DiscoveryConfig {
  baseUnit: number
  bankroll: number
  numShoes: number           // shoes per backtest (keep low for speed)
  targetMetric: 'profit_factor' | 'sharpe_ratio' | 'net_pnl' | 'roi'
  onProgress: (done: number, total: number, best: DiscoveryCandidate | null) => void
  signal: AbortSignal
}

// ────────────────────────────────────────────────────────────
// Candidate builder
// ────────────────────────────────────────────────────────────

let _idCounter = 0
function uid() { return `disc_${++_idCounter}_${Math.random().toString(36).slice(2,6)}` }

interface CandidateSpec {
  side: BetSide
  streakLen: number
  prog: ProgressionMethod
  sl: number
  tp: number
}

function candidateRules({ side, streakLen, prog, sl, tp }: CandidateSpec, bankroll: number): Rule[] {
  // Base bet rule
  const base: Rule = {
    id: uid(), priority: 1, enabled: true,
    label: `Bet ${side}`,
    trigger: { type: 'hand_count', hand_min: 1 },
    action:  { type: 'place_bet', side, unit_size: 1 },
    modifiers: { shoe_reset: 'reset' },
  }

  // Progression rule and its reset after a win (only when not flat)
  const progression: Rule[] = prog === 'flat' ? [] : [
    {
      id: uid(), priority: 2, enabled: true,
      label: `${prog} after ${streakLen} losses`,
      trigger: {
        type: 'streak', side: 'Any',
        direction: 'consecutive_losses',
        min_length: streakLen,
      },
      action: { type: 'adjust_unit', method: prog, value: 2 },
      modifiers: { max_bet: bankroll * 0.1 },
    },
    {
      id: uid(), priority: 3, enabled: true,
      label: 'Reset after win',
      trigger: { type: 'streak', side: 'Any', direction: 'consecutive_wins', min_length: 1 },
      action:  { type: 'reset_progression', reset_to: 1 },
      modifiers: { shoe_reset: 'carry' },
    },
  ]

  return [
    base,
    ...progression,
    {
      id: uid(), priority: 9, enabled: true,
      label: `Stop loss $${sl}`,
      trigger: { type: 'financial_state', condition: 'session_loss', threshold: -sl },
      action:  { type: 'stop_loss', threshold: -sl },
      modifiers: {},
    },
    {
      id: uid(), priority: 8, enabled: true,
      label: `Take profit $${tp}`,
      trigger: { type: 'financial_state', condition: 'session_profit', threshold: tp },
      action:  { type: 'take_profit', threshold: tp },
      modifiers: {},
    },
  ]
}

function buildCandidate(spec: CandidateSpec, baseUnit: number, bankroll: number): DiscoveryCandidate {
  const rules = candidateRules(spec, bankroll)
  const progLabel = spec.prog === 'flat' ? 'flat' : spec.prog + '@' + spec.streakLen + 'L'
  const label = `${spec.side} · ${progLabel} · SL$${spec.sl}/TP$${spec.tp}`
  return {
    id: uid(),
    label,
    score: 0,
    strategy: {
      id: uid(),
      name: label,
      version: '1.0',
      base_unit: baseUnit,
      bankroll,
      rules,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  }
}

const RISK_LIMITS = STOP_LOSSES.flatMap(sl => TAKE_PROFITS.map(tp => ({ sl, tp })))

// Every combination of side, streak length, progression, stop loss and take
// profit, in that nesting order.
function candidateSpecs(): CandidateSpec[] {
  const specs: CandidateSpec[] = []
  for (const side of BET_SIDES) {
    for (const streakLen of STREAK_LENS) {
      for (const prog of PROGRESSIONS) {
        for (const { sl, tp } of RISK_LIMITS) specs.push({ side, streakLen, prog, sl, tp })
      }
    }
  }
  return specs
}

function buildCandidates(baseUnit: number, bankroll: number): DiscoveryCandidate[] {
  return candidateSpecs().map(spec => buildCandidate(spec, baseUnit, bankroll))
}

// ────────────────────────────────────────────────────────────
// Scoring
// ────────────────────────────────────────────────────────────

function score(r: BacktestResults, metric: DiscoveryConfig['targetMetric']): number {
  const m = r.metrics
  switch (metric) {
    case 'profit_factor': return m.profit_factor
    case 'sharpe_ratio':  return m.sharpe_ratio
    case 'net_pnl':       return m.net_pnl
    case 'roi':           return m.roi
    default:              return m.profit_factor
  }
}

// ────────────────────────────────────────────────────────────
// Main discovery run
// ────────────────────────────────────────────────────────────

export async function runDiscovery(cfg: DiscoveryConfig): Promise<DiscoveryCandidate[]> {
  const candidates = buildCandidates(cfg.baseUnit, cfg.bankroll)

  // Use a small shoe count to keep things fast; caller can do a deep dive on winners
  const simCfg: SimulationConfig = {
    num_shoes: cfg.numShoes,
    deck_count: 8,
    table_rules: 'commission_5',
    cut_card_position: 14,
    tie_handling: 'push',
    starting_bankroll: cfg.bankroll,
    random_seed: 42,
  }

  const results: DiscoveryCandidate[] = []
  let best: DiscoveryCandidate | null = null

  for (let i = 0; i < candidates.length; i++) {
    if (cfg.signal.aborted) break

    const c = candidates[i]
    try {
      // Candidates run one at a time so progress and abort stay responsive.
      const r = await runSimulation(c.strategy, simCfg, () => {}) // NOSONAR: sequential on purpose
      c.results = r
      c.score   = score(r, cfg.targetMetric)
      results.push(c)
      if (!best || c.score > best.score) best = c
    } catch {
      // skip failed candidates
    }

    cfg.onProgress(i + 1, candidates.length, best)

    // Yield to keep UI responsive
    if (i % 5 === 0) await new Promise(r => setTimeout(r, 0)) // NOSONAR: deliberate yield to the UI
  }

  return results
    .filter(c => c.results && Number.isFinite(c.score))
    .sort((a, b) => b.score - a.score)
}
